/**
 * Client-side aggregation helpers for the backtest Analytics tab.
 *
 * All functions are pure and operate on data already fetched by the result
 * pages — no API calls. Two input shapes are used:
 *
 *   trades   [{pnl, pnl_pct, exit_reason, exit_date, holding_days, ...}]
 *   windows  [{screener_name, live_metrics:{...}, live_trades:[...trades]}]
 *
 * Walk-forward runs attribute trades to a screener via the window they ran in
 * (a screener may be selected in several windows across the run).
 */

// Distinct, legible series colors — green / purple / blue / amber / … —
// matched to the mockup's stacked-bar + radar legend.
export const SCREENER_COLORS = [
  '#22c55e', '#a855f7', '#3b82f6', '#f59e0b',
  '#ec4899', '#14b8a6', '#ef4444', '#8b5cf6',
  '#64748b', '#eab308',
];

const monthOf = (d) => (d ? d.slice(0, 7) : null);
const mean = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

// ---------------------------------------------------------------------------
// Walk-forward headline — the risk-adjusted set, not just return
// ---------------------------------------------------------------------------

const SQRT_252 = Math.sqrt(252);
const RF_DAILY = 0.06 / 252;

function sampleStdev(xs) {
  if (xs.length < 2) return 0;
  const mu = mean(xs);
  const variance = xs.reduce((a, x) => a + (x - mu) ** 2, 0) / (xs.length - 1);
  return variance > 0 ? Math.sqrt(variance) : 0;
}

function dailyReturns(curve) {
  const out = [];
  for (let i = 1; i < curve.length; i += 1) {
    const prev = curve[i - 1]?.value;
    if (!prev) continue;
    out.push((curve[i].value - prev) / prev);
  }
  return out;
}

/**
 * Headline metrics for a WF run, preferring what the backend computed.
 *
 * The backend writes analytics.risk_metrics when a run finishes, using the
 * same arithmetic as every other surface. The client-side fallback exists only
 * so a run still in progress shows something; it mirrors the backend formulas
 * (sample stdev, annualization capped at one year) so the numbers do not shift
 * when the run completes and the server figures take over.
 *
 * Returns null when there is nothing to summarise yet.
 */
export function wfHeadline(wfRun, windows = []) {
  if (!wfRun) return null;
  const server = wfRun.analytics?.risk_metrics;
  if (server) return { ...server, source: 'server' };

  const completed = windows.filter((w) => w.final_funds != null);
  if (!completed.length) return null;

  const initial = wfRun.initial_funds;
  const final = completed[completed.length - 1].final_funds;
  const curve = windows.flatMap((w) => w.equity_curve || []);
  const rets = dailyReturns(curve);

  let peak = -Infinity;
  let maxDD = 0;
  for (const pt of curve) {
    if (pt.value > peak) peak = pt.value;
    const dd = peak > 0 ? ((peak - pt.value) / peak) * 100 : 0;
    if (dd > maxDD) maxDD = dd;
  }

  // Calendar span, and no extrapolation below a year.
  let years = 0;
  if (curve.length >= 2) {
    const d0 = new Date(String(curve[0].date).slice(0, 10));
    const d1 = new Date(String(curve[curve.length - 1].date).slice(0, 10));
    years = (d1 - d0) / (1000 * 60 * 60 * 24 * 365.25);
  }
  const isAnn = years >= 1;
  const cagr = initial && final > 0 && years > 0
    ? (isAnn ? ((final / initial) ** (1 / years) - 1) * 100 : (final / initial - 1) * 100)
    : null;

  const std = sampleStdev(rets);
  const mu = rets.length ? mean(rets) : 0;
  const sharpe = std ? ((mu - RF_DAILY) / std) * SQRT_252 : null;

  const shortfalls = rets.map((r) => Math.min(r - RF_DAILY, 0));
  const downside = rets.length > 1
    ? Math.sqrt(shortfalls.reduce((a, x) => a + x * x, 0) / (shortfalls.length - 1))
    : 0;
  const sortino = downside ? ((mu - RF_DAILY) / downside) * SQRT_252 : null;

  // Exposure needs `invested`, which curves persisted before it existed omit.
  const withInvested = curve.filter((pt) => pt.invested != null);
  const held = withInvested.filter((pt) => pt.invested > 0).length;
  const timeInMarket = withInvested.length ? (held / withInvested.length) * 100 : null;
  const avgExposure = withInvested.length
    ? mean(withInvested.filter((pt) => pt.value).map((pt) => (pt.invested / pt.value) * 100))
    : null;

  const allTrades = windows.flatMap((w) => w.live_trades || []);
  const pcts = allTrades.map((t) => t.pnl_pct ?? 0);
  const losses = pcts.filter((p) => p <= 0);
  const avgLoss = losses.length ? Math.abs(mean(losses)) : 0;
  const expPct = pcts.length ? mean(pcts) : null;

  return {
    source: 'client',
    initial_funds: initial,
    final_value: final,
    total_return_pct: initial ? ((final - initial) / initial) * 100 : null,
    cagr_pct: cagr,
    cagr_is_annualized: isAnn,
    period_years: years,
    max_drawdown_pct: maxDD,
    sharpe_ratio: sharpe,
    sortino_ratio: sortino,
    active_sharpe_ratio: null,
    time_in_market_pct: timeInMarket,
    avg_exposure_pct: avgExposure,
    total_trades: allTrades.length,
    expectancy_pct: expPct,
    expectancy_r: expPct != null && avgLoss ? expPct / avgLoss : null,
    bootstrap: null,
  };
}


// ---------------------------------------------------------------------------
// Per-screener aggregates (WF runs) — feeds the radar + contribution legend
// ---------------------------------------------------------------------------

export function screenerAggregates(windows = []) {
  const by = new Map();
  for (const w of windows) {
    const name = w.screener_name || 'Unknown';
    if (!by.has(name)) by.set(name, { name, trades: [], sharpes: [], drawdowns: [] });
    const g = by.get(name);
    g.trades.push(...(w.live_trades || []));
    const lm = w.live_metrics || {};
    if (lm.sharpe_ratio != null) g.sharpes.push(lm.sharpe_ratio);
    if (lm.max_drawdown_pct != null) g.drawdowns.push(lm.max_drawdown_pct);
  }

  const rows = [...by.values()].map((g) => {
    const n = g.trades.length;
    const wins = g.trades.filter((t) => (t.pnl ?? 0) > 0).length;
    return {
      name: g.name,
      trades: n,
      winRate: n ? (wins / n) * 100 : 0,
      avgPnlPct: mean(g.trades.map((t) => t.pnl_pct ?? 0)),
      totalPnl: g.trades.reduce((a, t) => a + (t.pnl ?? 0), 0),
      avgSharpe: mean(g.sharpes),
      avgDrawdown: mean(g.drawdowns),
    };
  });

  rows.sort((a, b) => b.totalPnl - a.totalPnl);
  return rows.map((r, i) => ({ ...r, color: SCREENER_COLORS[i % SCREENER_COLORS.length] }));
}

// ---------------------------------------------------------------------------
// Cumulative realized P&L by screener over month buckets — stacked bar
// ---------------------------------------------------------------------------

export function cumulativeContributionByScreener(windows = []) {
  // Collect attributed, exited trades.
  const items = [];
  for (const w of windows) {
    const name = w.screener_name || 'Unknown';
    for (const t of w.live_trades || []) {
      const m = monthOf(t.exit_date);
      if (m && t.pnl != null) items.push({ name, month: m, pnl: t.pnl });
    }
  }
  if (!items.length) return { buckets: [], series: [] };

  const buckets = [...new Set(items.map((i) => i.month))].sort();
  const names = screenerAggregates(windows).map((r) => r.name); // stable, ranked order
  const colorFor = (i) => SCREENER_COLORS[i % SCREENER_COLORS.length];

  const series = names.map((name, idx) => {
    let cum = 0;
    const values = buckets.map((b) => {
      cum += items
        .filter((i) => i.name === name && i.month === b)
        .reduce((a, i) => a + i.pnl, 0);
      return cum;
    });
    return { name, color: colorFor(idx), values };
  });

  return { buckets, series };
}

// ---------------------------------------------------------------------------
// Returns distribution — winner/loser histogram + risk/reward profile
// ---------------------------------------------------------------------------

const HIST_EDGES = [-Infinity, -15, -10, -5, 0, 5, 10, 15, 20, 30, Infinity];
const HIST_LABELS = ['<-15', '-15:-10', '-10:-5', '-5:0', '0:5', '5:10', '10:15', '15:20', '20:30', '>30'];

export function returnsHistogram(trades = []) {
  const buckets = HIST_LABELS.map((label) => ({ label, win: 0, loss: 0 }));
  const wins = [];
  const losses = [];

  for (const t of trades) {
    const r = t.pnl_pct;
    if (r == null) continue;
    let bi = HIST_EDGES.findIndex((edge, i) => i > 0 && r > HIST_EDGES[i - 1] && r <= edge) - 1;
    if (bi < 0) bi = 0;
    if (r > 0) { buckets[bi].win += 1; wins.push(r); }
    else { buckets[bi].loss += 1; losses.push(r); }
  }

  return {
    buckets,
    avgWin: mean(wins),
    avgLoss: mean(losses),
    nWins: wins.length,
    nLosses: losses.length,
  };
}

// ---------------------------------------------------------------------------
// Exit-type breakdown — donut + average-return bars
// ---------------------------------------------------------------------------

export function exitTypeStats(trades = []) {
  const total = trades.length;
  const by = new Map();
  for (const t of trades) {
    const reason = t.exit_reason || 'unknown';
    if (!by.has(reason)) by.set(reason, []);
    by.get(reason).push(t);
  }

  const rows = [...by.entries()].map(([reason, ts]) => {
    const wins = ts.filter((t) => (t.pnl ?? 0) > 0).length;
    return {
      reason,
      count: ts.length,
      pct: total ? (ts.length / total) * 100 : 0,
      winRate: ts.length ? (wins / ts.length) * 100 : 0,
      avgPnlPct: mean(ts.map((t) => t.pnl_pct ?? 0)),
      totalPnl: ts.reduce((a, t) => a + (t.pnl ?? 0), 0),
    };
  });

  rows.sort((a, b) => b.count - a.count);
  return rows;
}

// ---------------------------------------------------------------------------
// Win-rate trend over time — line chart
// ---------------------------------------------------------------------------

export function winRateTrend(trades = []) {
  const by = new Map();
  for (const t of trades) {
    const m = monthOf(t.exit_date);
    if (!m) continue;
    if (!by.has(m)) by.set(m, { wins: 0, total: 0 });
    const g = by.get(m);
    g.total += 1;
    if ((t.pnl ?? 0) > 0) g.wins += 1;
  }
  return [...by.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([bucket, g]) => ({ bucket, winRate: (g.wins / g.total) * 100, count: g.total }));
}

// Pretty-print an exit_reason: "take_profit" -> "Take Profit"
export function prettyReason(reason = '') {
  return reason.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
